import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Textarea from '@/components/ui/Textarea';
import Button from '@/components/ui/Button';
import { GOALS, GOAL_LABELS, SEXES, SEX_LABELS, optsFrom } from '@/lib/enums';
import { maskPhone, isValidPhone, isValidEmail, onlyDigits } from '@/lib/masks';

const empty = {
  name: '', email: '', phone: '', password: '', heightCm: '', birthdate: '',
  goal: 'hipertrofia', sex: '', weeklyFrequency: '', observations: '',
};

export default function StudentForm({ onSubmit, onCancel }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return setErr(t('students.form.requiredError'));
    if (!isValidEmail(form.email)) return setErr(t('common.invalidEmail'));
    if (form.phone && !isValidPhone(form.phone)) return setErr(t('common.invalidPhone'));
    setSaving(true);
    setErr(null);
    const payload = {
      ...form,
      email: form.email.trim(),
      phone: onlyDigits(form.phone) || undefined,
      heightCm: form.heightCm ? Number(form.heightCm) : undefined,
      weeklyFrequency: form.weeklyFrequency ? Number(form.weeklyFrequency) : undefined,
      birthdate: form.birthdate || undefined,
      sex: form.sex || undefined,
    };
    try {
      await onSubmit(payload);
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
        <Input label={t('students.form.name')} value={form.name} onChange={set('name')} />
        <Input label={t('students.form.email')} type="email" value={form.email} onChange={set('email')} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
        <Input label={t('students.form.phone')} value={form.phone} inputMode="tel" placeholder="(11) 98765-4321"
          onChange={(e) => setForm((f) => ({ ...f, phone: maskPhone(e.target.value) }))} />
        <Input label={t('students.form.initialPassword')} type="password" value={form.password} onChange={set('password')} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--space-3)' }}>
        <Input label={t('students.form.heightCm')} type="number" value={form.heightCm} onChange={set('heightCm')} />
        <Input label={t('students.form.birthdate')} type="date" value={form.birthdate} onChange={set('birthdate')} />
        <Input label={t('students.form.weeklyFrequency')} type="number" value={form.weeklyFrequency} onChange={set('weeklyFrequency')} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
        <Select label={t('students.form.goal')} value={form.goal} onChange={set('goal')} options={optsFrom(GOALS, GOAL_LABELS)} />
        <Select label={t('students.form.sex')} value={form.sex} onChange={set('sex')} placeholder="—" options={optsFrom(SEXES, SEX_LABELS)} />
      </div>
      <Textarea label={t('students.form.observations')} value={form.observations} onChange={set('observations')} rows={2} />
      {err && <span style={{ color: 'var(--danger)', fontSize: 13 }}>{err}</span>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
        <Button type="button" variant="ghost" onClick={onCancel}>{t('common.cancel')}</Button>
        <Button type="submit" loading={saving}>{t('common.save')}</Button>
      </div>
    </form>
  );
}
